import { FC, useMemo } from 'react'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined'
import EditIcon from '@mui/icons-material/Edit'
import { Button, Stack, StackProps, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import { CalendarIcon } from '@mui/x-date-pickers'
import { LocationChange } from '../../../api/models'
import { formatIsoTimestamp, formatIsoTimestampForLocation } from '../../../utils/dateUtils'
import { useRouteLocation } from '../../../hooks/useRouteLocation'
import { useAuthenticationContext } from '../../../auth/AuthenticationContextProvider'
import { MISSING_VALUE, WRITE_LOCATION_CHANGE_SCOPE } from '../../../constants'
import { StatusChip, StatusChipProps } from '../../../components/StatusChip/StatusChip'

interface AddressSchedulerListProps extends StackProps {
  onEditClicked?: (locationChange: LocationChange) => void
  addressChanges: LocationChange[]
}

function cleanUpEmail(email?: string) {
  if (email) {
    return email.replace(/samlp\|ss-onelogin\|/g, '')
  }
}

export const AddressChangesList: FC<AddressSchedulerListProps> = ({ sx, onEditClicked, addressChanges }) => {
  const authContext = useAuthenticationContext()
  const location = useRouteLocation()
  const userCanEditAddressChange = authContext.has(WRITE_LOCATION_CHANGE_SCOPE)
  const emailCellSx = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: 'inherit',
    maxWidth: '150px'
  } as const
  const timestampCellSx = {
    fontSize: 'inherit'
  } as const

  const mapStatus = (status: LocationChange['state']): StatusChipProps['status'] => {
    switch (status) {
      case 'pending':
        return 'warning'
      case 'completed':
        return 'confirm'
      case 'cancelled':
        return 'error'
      default:
        return 'unknown'
    }
  }

  const getStatusIcon = (status: LocationChange['state']) => {
    switch (status) {
      case 'pending':
        return <CalendarIcon color="inherit" />
      case 'completed':
        return <CheckCircleOutlineOutlined color="inherit" />
      case 'cancelled':
        return <CancelIcon color="inherit" />
    }
  }

  // Sort address changes by createdAt date (newest first)
  const sortedAddressChanges = useMemo(
    () =>
      [...addressChanges].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      }),
    [addressChanges]
  )

  return (
    <Stack useFlexGap spacing="var(--spacing-s)" sx={{ width: '100%', ...sx, bgcolor: 'var(--bg-surface)' }} data-testid="address-changes-list">
      <Typography variant="h2" fontSize="var(--size-xl)" fontWeight="500" color="text.primary" p="var(--spacing-s) var(--spacing-xs)">
        Scheduled Address Changes
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>NEW ADDRESS</TableCell>
              <TableCell>CHANGE DATE</TableCell>
              <TableCell>STATUS</TableCell>
              <TableCell>CHANGE CREATED BY</TableCell>
              <TableCell>CHANGE CREATED</TableCell>
              <TableCell>LAST UPDATED</TableCell>
              <TableCell>LAST UPDATED BY</TableCell>
              <TableCell>{/* Empty cell for the edit button */}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ fontSize: 'var(--size-s)' }}>
            {sortedAddressChanges.map((change, index) => {
              const createdByEmail = cleanUpEmail(change.createdBy) || MISSING_VALUE
              const updatedByEmail = cleanUpEmail(change.updatedBy) || MISSING_VALUE
              return (
                <TableRow key={index} data-testid={'address-change-row'}>
                  <TableCell sx={{ fontSize: 'inherit' }}>
                    {change.address.street1} {change.address.postalCode}
                  </TableCell>
                  <TableCell sx={timestampCellSx}>
                    {formatIsoTimestamp(change.effectiveAt, { format: 'f ZZZZ', timeZoneName: change.address.timeZone })}
                  </TableCell>
                  <TableCell>
                    <StatusChip label={change.state} status={mapStatus(change.state)} icon={getStatusIcon(change.state)} />
                  </TableCell>
                  <TableCell sx={emailCellSx} title={createdByEmail}>
                    <Tooltip title={createdByEmail} placement="top-start" arrow>
                      <span>{createdByEmail}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={timestampCellSx} data-testid={`address-change-created-at-${change.id}`}>
                    {formatIsoTimestampForLocation(change.createdAt, location)}
                  </TableCell>
                  <TableCell sx={timestampCellSx} data-testid={`address-change-updated-at-${change.id}`}>
                    {formatIsoTimestampForLocation(change.updatedAt, location)}
                  </TableCell>
                  <TableCell sx={emailCellSx} title={updatedByEmail}>
                    <Tooltip title={updatedByEmail} placement="top-start" arrow>
                      <span>{updatedByEmail}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {change.state === 'pending' && (
                      <Button
                        variant="text"
                        startIcon={<EditIcon />}
                        disabled={!userCanEditAddressChange}
                        onClick={() => (onEditClicked ? onEditClicked(change) : undefined)}
                        data-testid={`edit-change-address-button-${change.id}`}
                      >
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}
